import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { activities } from '../data/activities';

const ActivityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const activity = activities.find(a => a.id === id);

  if (!activity) {
    return (
      <div className="min-h-screen py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="font-serif text-4xl font-normal text-stone-900">Activity not found</h1>
          <Link to="/blog/activities" className="font-serif text-stone-800 hover:underline">
            ← Back to activities
          </Link>
        </div>
      </div>
    );
  }

  // Split description by image markers and render content with images
  const renderContent = () => {
    const parts = activity.description.split(/\[IMG:(\d+)\]/);
    return parts.map((part, index) => {
      if (index % 2 === 0) {
        // Text content
        return part && <p key={index} className="font-serif whitespace-pre-line mb-8 text-stone-500">{part}</p>;
      } else {
        // Image marker - render corresponding image
        const imageIndex = parseInt(part);
        return activity.images[imageIndex] && (
          <div key={index} className="my-8">
            <img
              src={activity.images[imageIndex]}
              alt={`${activity.title} - ${imageIndex + 1}`}
              className="w-full max-h-[600px] object-cover rounded-lg"
            />
          </div>
        );
      }
    });
  };

  return (
    <div className="min-h-screen py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/activities" className="font-serif text-stone-800 hover:underline mb-8 block">
            ← Back to Activities
          </Link>
          
          <article className="prose prose-lg max-w-none">
            <h1 className="font-serif text-5xl font-normal mb-6 text-stone-900">{activity.title}</h1>
            {activity.subtitle && (
              <p className="font-serif text-xl text-stone-500 italic mb-8">{activity.subtitle}</p>
            )}
            
            <div className="mb-10">
              <span className="font-serif text-stone-500">{activity.date}</span>
              <span className="mx-2 text-stone-500">•</span>
              <span className="font-serif text-stone-500">{activity.location}</span>
            </div>

            <div className="mb-10">
              {renderContent()}
            </div>

            <div className="bg-white border border-stone-200 p-8 rounded-lg">
              <h3 className="font-serif text-2xl font-normal mb-6 text-stone-900">Highlights</h3>
              <ul className="space-y-3">
                {activity.highlights.map((highlight, index) => (
                  <li key={index} className="font-serif flex items-start text-stone-500">
                    <span className="mr-3">•</span>
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </motion.div>
      </div>
    </div>
  );
};

export default ActivityDetail;
